import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { settingsAPI, adminAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useSettings } from '../contexts/SettingsContext';

const AdminSettings = () => {
  const { refreshSettings } = useSettings();
  const [settings, setSettings] = useState({
    site_name: '',
    about_content: '',
    footer_description: '',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    social_youtube: '',
    social_instagram: '',
    social_twitter: '',
    social_facebook: '',
    og_image: '',
    show_kariyer_link: true,
    show_kvkk_link: true,
    show_kullanim_kosullari_link: true,
    show_gizlilik_politikasi_link: true,
    google_analytics_script: '',
    primary_color: '#fdb714',
    now_showing_badge_color: '#fdb714',
    coming_soon_badge_color: '#9333ea',
    background_primary: '#000000',
    background_secondary: '#0a0a0a',
    text_primary: '#ffffff',
    text_secondary: '#d1d5db',
    text_muted: '#9ca3af',
    gradient_movie_from: '#3b82f6',
    gradient_movie_to: '#8b5cf6',
    gradient_salon_from: '#10b981',
    gradient_salon_to: '#f59e0b',
    gradient_campaign_from: '#a855f7',
    gradient_campaign_to: '#ec4899',
    gradient_showtime_from: '#f59e0b',
    gradient_showtime_to: '#ef4444',
    gradient_news_from: '#3b82f6',
    gradient_news_to: '#8b5cf6',
  });
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      toast({ title: 'Hata', description: 'Ayarlar yüklenemedi', variant: 'destructive' });
    }
  };

  const handleSaveSettings = async () => {
    try {
      await settingsAPI.update(settings);
      
      // Update localStorage cache
      localStorage.setItem('cinefora_settings', JSON.stringify(settings));
      
      // Refresh settings context
      await refreshSettings();
      
      // Helper to adjust color brightness
      const adjustColorBrightness = (hex, percent) => {
        hex = hex.replace(/^#/, '');
        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);
        r = Math.max(0, Math.min(255, r + (r * percent / 100)));
        g = Math.max(0, Math.min(255, g + (g * percent / 100)));
        b = Math.max(0, Math.min(255, b + (b * percent / 100)));
        return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
      };
      
      // Update CSS variables immediately
      document.documentElement.style.setProperty('--color-primary', settings.primary_color);
      document.documentElement.style.setProperty('--color-primary-hover', adjustColorBrightness(settings.primary_color || '#fdb714', -20));
      document.documentElement.style.setProperty('--color-now-showing', settings.now_showing_badge_color);
      document.documentElement.style.setProperty('--color-coming-soon', settings.coming_soon_badge_color);
      document.documentElement.style.setProperty('--color-bg-primary', settings.background_primary);
      document.documentElement.style.setProperty('--color-bg-secondary', settings.background_secondary);
      document.documentElement.style.setProperty('--color-text-primary', settings.text_primary);
      document.documentElement.style.setProperty('--color-text-secondary', settings.text_secondary);
      document.documentElement.style.setProperty('--color-text-muted', settings.text_muted);
      
      // Update gradient CSS variables
      document.documentElement.style.setProperty('--gradient-movie-from', settings.gradient_movie_from || '#3b82f6');
      document.documentElement.style.setProperty('--gradient-movie-to', settings.gradient_movie_to || '#8b5cf6');
      document.documentElement.style.setProperty('--gradient-salon-from', settings.gradient_salon_from || '#10b981');
      document.documentElement.style.setProperty('--gradient-salon-to', settings.gradient_salon_to || '#f59e0b');
      document.documentElement.style.setProperty('--gradient-campaign-from', settings.gradient_campaign_from || '#a855f7');
      document.documentElement.style.setProperty('--gradient-campaign-to', settings.gradient_campaign_to || '#ec4899');
      document.documentElement.style.setProperty('--gradient-showtime-from', settings.gradient_showtime_from || '#f59e0b');
      document.documentElement.style.setProperty('--gradient-showtime-to', settings.gradient_showtime_to || '#ef4444');
      document.documentElement.style.setProperty('--gradient-news-from', settings.gradient_news_from || '#3b82f6');
      document.documentElement.style.setProperty('--gradient-news-to', settings.gradient_news_to || '#8b5cf6');
      
      toast({ title: 'Başarılı', description: 'Ayarlar güncellendi. Sayfa yenileniyor...' });
      // Reload to apply changes everywhere
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast({ title: 'Hata', description: 'Ayarlar kaydedilemedi', variant: 'destructive' });
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast({ title: 'Hata', description: 'Yeni şifreler eşleşmiyor', variant: 'destructive' });
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast({ title: 'Hata', description: 'Şifre en az 6 karakter olmalı', variant: 'destructive' });
      return;
    }

    try {
      await adminAPI.changePassword({
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
      });
      toast({ title: 'Başarılı', description: 'Şifre değiştirildi' });
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast({ 
        title: 'Hata', 
        description: error.response?.data?.detail || 'Şifre değiştirilemedi',
        variant: 'destructive' 
      });
    }
  };

  return (
    <AdminLayout>
      <div>
        <h1 className="text-3xl font-bold text-white mb-8">Ayarlar</h1>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="bg-gray-900 border border-gray-800">
            <TabsTrigger value="general" className="data-[state=active]:bg-[#FDB714] data-[state=active]:text-black">
              Genel Ayarlar
            </TabsTrigger>
            <TabsTrigger value="about" className="data-[state=active]:bg-[#FDB714] data-[state=active]:text-black">
              Hakkında
            </TabsTrigger>
            <TabsTrigger value="footer" className="data-[state=active]:bg-[#FDB714] data-[state=active]:text-black">
              Footer Ayarları
            </TabsTrigger>
            <TabsTrigger value="social" className="data-[state=active]:bg-[#FDB714] data-[state=active]:text-black">
              Sosyal Medya
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#FDB714] data-[state=active]:text-black">
              Google Analytics
            </TabsTrigger>
            <TabsTrigger value="theme" className="data-[state=active]:bg-[#FDB714] data-[state=active]:text-black">
              Tema Renkleri
            </TabsTrigger>
            <TabsTrigger value="password" className="data-[state=active]:bg-[#FDB714] data-[state=active]:text-black">
              Şifre Değiştir
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <Card className="bg-gray-900 border-gray-800 p-6">
              <div className="space-y-4">
                <div>
                  <Label>Site Adı</Label>
                  <Input
                    value={settings.site_name}
                    onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div>
                  <Label>İletişim E-posta</Label>
                  <Input
                    type="email"
                    value={settings.contact_email}
                    onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div>
                  <Label>İletişim Telefon</Label>
                  <Input
                    value={settings.contact_phone}
                    onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div>
                  <Label>Adres Bilgisi</Label>
                  <Textarea
                    value={settings.contact_address}
                    onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    rows={2}
                    placeholder="Örn: Türkiye genelinde 73 sinema salonumuzla hizmetinizdeyiz"
                  />
                </div>

                <Button 
                  onClick={handleSaveSettings}
                  className="bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold"
                >
                  Kaydet
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="about">
            <Card className="bg-gray-900 border-gray-800 p-6">
              <div className="space-y-4">
                <div>
                  <Label>Hakkında İçeriği</Label>
                  <Textarea
                    value={settings.about_content}
                    onChange={(e) => setSettings({ ...settings, about_content: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white min-h-[300px]"
                    placeholder="Hakkında sayfası için içerik girin..."
                  />
                </div>

                <Button 
                  onClick={handleSaveSettings}
                  className="bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold"
                >
                  Kaydet
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="footer">
            <Card className="bg-gray-900 border-gray-800 p-6">
              <div className="space-y-4">
                <div>
                  <Label>Footer Açıklama</Label>
                  <Textarea
                    value={settings.footer_description}
                    onChange={(e) => setSettings({ ...settings, footer_description: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    rows={3}
                    placeholder="Footer'da görünecek kısa açıklama..."
                  />
                </div>

                <div className="border-t border-gray-700 pt-4">
                  <h4 className="text-white font-semibold mb-2">Kurumsal Linkler Görünürlüğü</h4>
                  <p className="text-gray-400 text-sm mb-3">
                    Bu linkler Footer'da görünecektir. Link olarak çalışması için "Sayfalar" bölümünden aynı isimde sayfa eklemeniz gerekir.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="show_kariyer"
                        checked={settings.show_kariyer_link}
                        onChange={(e) => setSettings({ ...settings, show_kariyer_link: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="show_kariyer" className="cursor-pointer">Kariyer Linkini Göster</Label>
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="show_kvkk"
                        checked={settings.show_kvkk_link}
                        onChange={(e) => setSettings({ ...settings, show_kvkk_link: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="show_kvkk" className="cursor-pointer">KVKK Linkini Göster</Label>
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="show_kullanim"
                        checked={settings.show_kullanim_kosullari_link}
                        onChange={(e) => setSettings({ ...settings, show_kullanim_kosullari_link: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="show_kullanim" className="cursor-pointer">Kullanım Koşulları Linkini Göster</Label>
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="show_gizlilik"
                        checked={settings.show_gizlilik_politikasi_link}
                        onChange={(e) => setSettings({ ...settings, show_gizlilik_politikasi_link: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="show_gizlilik" className="cursor-pointer">Gizlilik Politikası Linkini Göster</Label>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={handleSaveSettings}
                  className="bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold"
                >
                  Kaydet
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="social">
            <Card className="bg-gray-900 border-gray-800 p-6">
              <div className="space-y-4">
                <div>
                  <Label>YouTube URL</Label>
                  <Input
                    value={settings.social_youtube}
                    onChange={(e) => setSettings({ ...settings, social_youtube: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div>
                  <Label>Instagram URL</Label>
                  <Input
                    value={settings.social_instagram}
                    onChange={(e) => setSettings({ ...settings, social_instagram: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div>
                  <Label>Twitter/X URL</Label>
                  <Input
                    value={settings.social_twitter}
                    onChange={(e) => setSettings({ ...settings, social_twitter: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div>
                  <Label>Facebook URL</Label>
                  <Input
                    value={settings.social_facebook}
                    onChange={(e) => setSettings({ ...settings, social_facebook: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>

                <div className="border-t border-gray-700 pt-4 mt-4">
                  <Label>Sosyal Medya Paylaşım Görseli URL</Label>
                  <Input
                    value={settings.og_image || ''}
                    onChange={(e) => setSettings({ ...settings, og_image: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="https://cinefora.com.tr/og-image.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Facebook, Twitter gibi sosyal medyalarda paylaşıldığında gösterilecek görsel. Önerilen boyut: 1200x630px
                  </p>
                </div>

                <Button 
                  onClick={handleSaveSettings}
                  className="bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold"
                >
                  Kaydet
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <Card className="bg-gray-900 border-gray-800 p-6">
              <div className="space-y-4">
                <div>
                  <Label>Google Analytics / Tag Manager Script</Label>
                  <Textarea
                    value={settings.google_analytics_script}
                    onChange={(e) => setSettings({ ...settings, google_analytics_script: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white font-mono text-sm"
                    rows={12}
                    placeholder={`<!-- Google Analytics veya Tag Manager script'ini buraya yapıştırın -->
<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'G-XXXXXXXXXX');
</script>`}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Bu script tüm sayfalarda {'<head>'} içinde otomatik olarak yüklenecektir.
                  </p>
                </div>

                <Button 
                  onClick={handleSaveSettings}
                  className="bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold"
                >
                  Kaydet
                </Button>
              </div>
            </Card>
          </TabsContent>



          <TabsContent value="theme">
            <Card className="bg-gray-900 border-gray-800 p-6">
              <h2 className="text-xl font-bold text-white mb-4">Tema Renkleri</h2>
              <p className="text-gray-400 mb-6 text-sm">
                Site genelinde kullanılan renkleri buradan özelleştirebilirsiniz. Değişiklikler anında yansıyacaktır.
              </p>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-white mb-2 block">Ana Renk (Primary)</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.primary_color}
                        onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                        className="w-16 h-16 rounded cursor-pointer border-2 border-gray-700"
                      />
                      <div className="flex-1">
                        <Input
                          value={settings.primary_color}
                          onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white font-mono"
                          placeholder="#fdb714"
                        />
                        <p className="text-gray-500 text-xs mt-1">Butonlar, linkler ve vurgular için kullanılır</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">Vizyonda Badge Rengi</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.now_showing_badge_color}
                        onChange={(e) => setSettings({ ...settings, now_showing_badge_color: e.target.value })}
                        className="w-16 h-16 rounded cursor-pointer border-2 border-gray-700"
                      />
                      <div className="flex-1">
                        <Input
                          value={settings.now_showing_badge_color}
                          onChange={(e) => setSettings({ ...settings, now_showing_badge_color: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white font-mono"
                          placeholder="#fdb714"
                        />
                        <p className="text-gray-500 text-xs mt-1">"Vizyonda" etiketlerinde kullanılır</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">Yakında Badge Rengi</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.coming_soon_badge_color}
                        onChange={(e) => setSettings({ ...settings, coming_soon_badge_color: e.target.value })}
                        className="w-16 h-16 rounded cursor-pointer border-2 border-gray-700"
                      />
                      <div className="flex-1">
                        <Input
                          value={settings.coming_soon_badge_color}
                          onChange={(e) => setSettings({ ...settings, coming_soon_badge_color: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white font-mono"
                          placeholder="#9333ea"
                        />
                        <p className="text-gray-500 text-xs mt-1">"Yakında" etiketlerinde kullanılır</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">Ana Arka Plan</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.background_primary}
                        onChange={(e) => setSettings({ ...settings, background_primary: e.target.value })}
                        className="w-16 h-16 rounded cursor-pointer border-2 border-gray-700"
                      />
                      <div className="flex-1">
                        <Input
                          value={settings.background_primary}
                          onChange={(e) => setSettings({ ...settings, background_primary: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white font-mono"
                          placeholder="#000000"
                        />
                        <p className="text-gray-500 text-xs mt-1">Sayfa arka plan rengi</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-white mb-2 block">İkincil Arka Plan</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.background_secondary}
                        onChange={(e) => setSettings({ ...settings, background_secondary: e.target.value })}
                        className="w-16 h-16 rounded cursor-pointer border-2 border-gray-700"
                      />
                      <div className="flex-1">
                        <Input
                          value={settings.background_secondary}
                          onChange={(e) => setSettings({ ...settings, background_secondary: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white font-mono"
                          placeholder="#0a0a0a"
                        />
                        <p className="text-gray-500 text-xs mt-1">Kartlar ve panellerde kullanılır</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-800 pt-6 mt-6">
                  <h3 className="text-lg font-bold text-white mb-4">Font Renkleri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-white mb-2 block">Ana Metin Rengi</Label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={settings.text_primary}
                          onChange={(e) => setSettings({ ...settings, text_primary: e.target.value })}
                          className="w-16 h-16 rounded cursor-pointer border-2 border-gray-700"
                        />
                        <div className="flex-1">
                          <Input
                            value={settings.text_primary}
                            onChange={(e) => setSettings({ ...settings, text_primary: e.target.value })}
                            className="bg-gray-800 border-gray-700 text-white font-mono"
                            placeholder="#ffffff"
                          />
                          <p className="text-gray-500 text-xs mt-1">Başlıklar ve önemli metinler</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label className="text-white mb-2 block">İkincil Metin Rengi</Label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={settings.text_secondary}
                          onChange={(e) => setSettings({ ...settings, text_secondary: e.target.value })}
                          className="w-16 h-16 rounded cursor-pointer border-2 border-gray-700"
                        />
                        <div className="flex-1">
                          <Input
                            value={settings.text_secondary}
                            onChange={(e) => setSettings({ ...settings, text_secondary: e.target.value })}
                            className="bg-gray-800 border-gray-700 text-white font-mono"
                            placeholder="#d1d5db"
                          />
                          <p className="text-gray-500 text-xs mt-1">Açıklama ve normal metinler</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label className="text-white mb-2 block">Yardımcı Metin Rengi</Label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={settings.text_muted}
                          onChange={(e) => setSettings({ ...settings, text_muted: e.target.value })}
                          className="w-16 h-16 rounded cursor-pointer border-2 border-gray-700"
                        />
                        <div className="flex-1">
                          <Input
                            value={settings.text_muted}
                            onChange={(e) => setSettings({ ...settings, text_muted: e.target.value })}
                            className="bg-gray-800 border-gray-700 text-white font-mono"
                            placeholder="#9ca3af"
                          />
                          <p className="text-gray-500 text-xs mt-1">İpuçları ve küçük bilgiler</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-800 pt-6 mt-6">
                  <h3 className="text-lg font-bold text-white mb-4">🎬 Gradient Renkleri (Sinema Teması)</h3>
                  <p className="text-gray-400 mb-4 text-sm">
                    Film kartları, salon kartları, kampanya kartları ve seans butonlarında kullanılan gradient renkleri özelleştirin.
                  </p>
                  
                  <div className="space-y-6">
                    {/* Film Kartları Gradient */}
                    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        🎬 Film Kartları Gradient
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Başlangıç Rengi</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={settings.gradient_movie_from || '#3b82f6'}
                              onChange={(e) => setSettings({ ...settings, gradient_movie_from: e.target.value })}
                              className="w-12 h-12 rounded cursor-pointer border-2 border-gray-600"
                            />
                            <Input
                              value={settings.gradient_movie_from || '#3b82f6'}
                              onChange={(e) => setSettings({ ...settings, gradient_movie_from: e.target.value })}
                              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                              placeholder="#3b82f6"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Bitiş Rengi</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={settings.gradient_movie_to || '#8b5cf6'}
                              onChange={(e) => setSettings({ ...settings, gradient_movie_to: e.target.value })}
                              className="w-12 h-12 rounded cursor-pointer border-2 border-gray-600"
                            />
                            <Input
                              value={settings.gradient_movie_to || '#8b5cf6'}
                              onChange={(e) => setSettings({ ...settings, gradient_movie_to: e.target.value })}
                              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                              placeholder="#8b5cf6"
                            />
                          </div>
                        </div>
                      </div>
                      <div 
                        className="mt-3 h-16 rounded-lg flex items-center justify-center text-white font-semibold"
                        style={{ 
                          background: `linear-gradient(135deg, ${settings.gradient_movie_from || '#3b82f6'} 0%, ${settings.gradient_movie_to || '#8b5cf6'} 100%)`
                        }}
                      >
                        Film Kartı Önizleme
                      </div>
                    </div>

                    {/* Salon Kartları Gradient */}
                    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        🏛️ Salon Kartları Gradient
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Başlangıç Rengi</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={settings.gradient_salon_from || '#10b981'}
                              onChange={(e) => setSettings({ ...settings, gradient_salon_from: e.target.value })}
                              className="w-12 h-12 rounded cursor-pointer border-2 border-gray-600"
                            />
                            <Input
                              value={settings.gradient_salon_from || '#10b981'}
                              onChange={(e) => setSettings({ ...settings, gradient_salon_from: e.target.value })}
                              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                              placeholder="#10b981"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Bitiş Rengi</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={settings.gradient_salon_to || '#f59e0b'}
                              onChange={(e) => setSettings({ ...settings, gradient_salon_to: e.target.value })}
                              className="w-12 h-12 rounded cursor-pointer border-2 border-gray-600"
                            />
                            <Input
                              value={settings.gradient_salon_to || '#f59e0b'}
                              onChange={(e) => setSettings({ ...settings, gradient_salon_to: e.target.value })}
                              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                              placeholder="#f59e0b"
                            />
                          </div>
                        </div>
                      </div>
                      <div 
                        className="mt-3 h-16 rounded-lg flex items-center justify-center text-white font-semibold"
                        style={{ 
                          background: `linear-gradient(135deg, ${settings.gradient_salon_from || '#10b981'} 0%, ${settings.gradient_salon_to || '#f59e0b'} 100%)`
                        }}
                      >
                        Salon Kartı Önizleme
                      </div>
                    </div>

                    {/* Kampanya Kartları Gradient */}
                    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        🎉 Kampanya Kartları Gradient
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Başlangıç Rengi</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={settings.gradient_campaign_from || '#a855f7'}
                              onChange={(e) => setSettings({ ...settings, gradient_campaign_from: e.target.value })}
                              className="w-12 h-12 rounded cursor-pointer border-2 border-gray-600"
                            />
                            <Input
                              value={settings.gradient_campaign_from || '#a855f7'}
                              onChange={(e) => setSettings({ ...settings, gradient_campaign_from: e.target.value })}
                              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                              placeholder="#a855f7"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Bitiş Rengi</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={settings.gradient_campaign_to || '#ec4899'}
                              onChange={(e) => setSettings({ ...settings, gradient_campaign_to: e.target.value })}
                              className="w-12 h-12 rounded cursor-pointer border-2 border-gray-600"
                            />
                            <Input
                              value={settings.gradient_campaign_to || '#ec4899'}
                              onChange={(e) => setSettings({ ...settings, gradient_campaign_to: e.target.value })}
                              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                              placeholder="#ec4899"
                            />
                          </div>
                        </div>
                      </div>
                      <div 
                        className="mt-3 h-16 rounded-lg flex items-center justify-center text-white font-semibold"
                        style={{ 
                          background: `linear-gradient(135deg, ${settings.gradient_campaign_from || '#a855f7'} 0%, ${settings.gradient_campaign_to || '#ec4899'} 100%)`
                        }}
                      >
                        Kampanya Kartı Önizleme
                      </div>
                    </div>

                    {/* Seans Butonları Gradient */}
                    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        🎟️ Seans Butonları Gradient
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Başlangıç Rengi</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={settings.gradient_showtime_from || '#f59e0b'}
                              onChange={(e) => setSettings({ ...settings, gradient_showtime_from: e.target.value })}
                              className="w-12 h-12 rounded cursor-pointer border-2 border-gray-600"
                            />
                            <Input
                              value={settings.gradient_showtime_from || '#f59e0b'}
                              onChange={(e) => setSettings({ ...settings, gradient_showtime_from: e.target.value })}
                              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                              placeholder="#f59e0b"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Bitiş Rengi</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={settings.gradient_showtime_to || '#ef4444'}
                              onChange={(e) => setSettings({ ...settings, gradient_showtime_to: e.target.value })}
                              className="w-12 h-12 rounded cursor-pointer border-2 border-gray-600"
                            />
                            <Input
                              value={settings.gradient_showtime_to || '#ef4444'}
                              onChange={(e) => setSettings({ ...settings, gradient_showtime_to: e.target.value })}
                              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                              placeholder="#ef4444"
                            />
                          </div>
                        </div>
                      </div>
                      <div 
                        className="mt-3 h-16 rounded-lg flex items-center justify-center text-white font-semibold"
                        style={{ 
                          background: `linear-gradient(135deg, ${settings.gradient_showtime_from || '#f59e0b'} 0%, ${settings.gradient_showtime_to || '#ef4444'} 100%)`
                        }}
                      >
                        Seans Butonu Önizleme
                      </div>
                    </div>

                    {/* Haber Kartları Gradient */}
                    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                        📰 Haber Kartları Gradient
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Başlangıç Rengi</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={settings.gradient_news_from || '#3b82f6'}
                              onChange={(e) => setSettings({ ...settings, gradient_news_from: e.target.value })}
                              className="w-12 h-12 rounded cursor-pointer border-2 border-gray-600"
                            />
                            <Input
                              value={settings.gradient_news_from || '#3b82f6'}
                              onChange={(e) => setSettings({ ...settings, gradient_news_from: e.target.value })}
                              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                              placeholder="#3b82f6"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-gray-300 mb-2 block text-sm">Bitiş Rengi</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={settings.gradient_news_to || '#8b5cf6'}
                              onChange={(e) => setSettings({ ...settings, gradient_news_to: e.target.value })}
                              className="w-12 h-12 rounded cursor-pointer border-2 border-gray-600"
                            />
                            <Input
                              value={settings.gradient_news_to || '#8b5cf6'}
                              onChange={(e) => setSettings({ ...settings, gradient_news_to: e.target.value })}
                              className="bg-gray-700 border-gray-600 text-white font-mono text-sm"
                              placeholder="#8b5cf6"
                            />
                          </div>
                        </div>
                      </div>
                      <div 
                        className="mt-3 h-16 rounded-lg flex items-center justify-center text-white font-semibold"
                        style={{ 
                          background: `linear-gradient(135deg, ${settings.gradient_news_from || '#3b82f6'} 0%, ${settings.gradient_news_to || '#8b5cf6'} 100%)`
                        }}
                      >
                        Haber Kartı Önizleme
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-800 pt-6 mt-6">
                  <div className="bg-gray-800 rounded-lg p-4 mb-4">
                    <h3 className="text-white font-semibold mb-3">Önizleme</h3>
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-3">
                        <div 
                          className="px-4 py-2 rounded font-medium"
                          style={{ 
                            backgroundColor: settings.primary_color,
                            color: '#000000'
                          }}
                        >
                          Ana Renk Butonu
                        </div>
                        <div 
                          className="px-3 py-1 rounded text-sm"
                          style={{ 
                            backgroundColor: settings.now_showing_badge_color,
                            color: '#ffffff'
                          }}
                        >
                          Vizyonda
                        </div>
                        <div 
                          className="px-3 py-1 rounded text-sm"
                          style={{ 
                            backgroundColor: settings.coming_soon_badge_color,
                            color: '#ffffff'
                          }}
                        >
                          Yakında
                        </div>
                        <div 
                          className="px-4 py-2 rounded border"
                          style={{ 
                            backgroundColor: settings.background_secondary,
                            borderColor: settings.primary_color,
                            color: settings.text_primary
                          }}
                        >
                          İkincil Arka Plan
                        </div>
                      </div>
                      
                      <div 
                        className="p-4 rounded"
                        style={{ backgroundColor: settings.background_secondary }}
                      >
                        <h4 
                          className="font-bold text-xl mb-2"
                          style={{ color: settings.text_primary }}
                        >
                          Ana Başlık Rengi
                        </h4>
                        <p 
                          className="mb-2"
                          style={{ color: settings.text_secondary }}
                        >
                          Bu bir ikincil metin örneğidir. Normal açıklamalarda kullanılır.
                        </p>
                        <p 
                          className="text-sm"
                          style={{ color: settings.text_muted }}
                        >
                          Bu bir yardımcı metin örneğidir. İpuçları ve küçük bilgiler için kullanılır.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      onClick={handleSaveSettings}
                      style={{ backgroundColor: settings.primary_color }}
                      className="text-black hover:opacity-90"
                    >
                      Tema Renklerini Kaydet
                    </Button>
                    <Button 
                      onClick={() => {
                        setSettings({
                          ...settings,
                          primary_color: '#fdb714',
                          now_showing_badge_color: '#fdb714',
                          coming_soon_badge_color: '#9333ea',
                          background_primary: '#000000',
                          background_secondary: '#0a0a0a',
                          text_primary: '#ffffff',
                          text_secondary: '#d1d5db',
                          text_muted: '#9ca3af',
                          gradient_movie_from: '#3b82f6',
                          gradient_movie_to: '#8b5cf6',
                          gradient_salon_from: '#10b981',
                          gradient_salon_to: '#f59e0b',
                          gradient_campaign_from: '#a855f7',
                          gradient_campaign_to: '#ec4899',
                          gradient_showtime_from: '#f59e0b',
                          gradient_showtime_to: '#ef4444',
                          gradient_news_from: '#3b82f6',
                          gradient_news_to: '#8b5cf6',
                        });
                      }}
                      className="bg-gray-700 hover:bg-gray-600 text-white"
                    >
                      Varsayılana Sıfırla
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="password">
            <Card className="bg-gray-900 border-gray-800 p-6">
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label>Eski Şifre</Label>
                  <Input
                    type="password"
                    value={passwordData.old_password}
                    onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    required
                  />
                </div>

                <div>
                  <Label>Yeni Şifre</Label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    required
                  />
                </div>

                <div>
                  <Label>Yeni Şifre (Tekrar)</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    required
                  />
                </div>

                <Button 
                  type="submit"
                  className="bg-[#FDB714] hover:bg-[#e5a510] text-black font-semibold"
                >
                  Şifreyi Değiştir
                </Button>
              </form>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
